/**
 * Forgot Password page — /forgot-password
 *
 * Collects the user's email and POSTs to /api/auth/forgot-password. Backend
 * always returns the same neutral response (no email enumeration), so we show
 * the same confirmation UI whether the address is registered or not.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api/client';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      setSent(true);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not send reset email — please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <Link to="/login" data-testid="forgot-back-link" className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-[#F46A25] font-semibold mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to sign in
      </Link>

      <div className="bg-white border border-neutral-200 rounded-sm p-6 md:p-8">
        {sent ? (
          <div data-testid="forgot-sent-card">
            <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <h1 className="text-2xl font-black text-[#12263A] mb-2">Check your email</h1>
            <p className="text-sm text-neutral-700 leading-relaxed mb-4">
              If an account exists for <b>{email}</b>, we've sent a link you can click to choose a new password.
              The link is valid for <b>30 minutes</b>.
            </p>
            <p className="text-xs text-neutral-500">
              Not in your inbox? Check your spam folder or{' '}
              <button
                type="button"
                onClick={() => setSent(false)}
                data-testid="forgot-try-again"
                className="text-[#F46A25] font-semibold hover:underline"
              >try a different email</button>.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <h1 className="text-2xl font-black text-[#12263A] mb-1">Forgot password?</h1>
            <p className="text-sm text-neutral-600 mb-4">
              Enter the email address you signed up with. We'll email you a link to set a new password.
            </p>
            <div>
              <label className="block text-sm font-semibold text-[#12263A] mb-1">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                <input
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  type="email"
                  required
                  autoFocus
                  placeholder="you@example.com"
                  data-testid="forgot-email-input"
                  className={`w-full border rounded-sm py-2.5 pl-10 pr-3 text-sm focus:outline-none ${error ? 'border-red-500 focus:border-red-500' : 'border-neutral-300 focus:border-[#F46A25]'}`}
                />
              </div>
              {error && (
                <p
                  data-testid="forgot-error-msg"
                  role="alert"
                  className="mt-2 flex items-start gap-1.5 text-xs font-semibold text-red-600"
                >
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  {error}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={loading || !email.trim()}
              data-testid="forgot-submit-btn"
              className="w-full bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold py-3 rounded-sm transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : 'Send reset link'}
            </button>
            <p className="text-xs text-neutral-500 text-center pt-2">
              Remembered it? <Link to="/login" className="text-[#F46A25] font-semibold hover:underline">Sign in instead</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
